import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor,
  Download,
  CheckCircle2,
  Copy,
  ExternalLink,
  AlertCircle,
  ClipboardList,
} from 'lucide-react';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { useRecordingConfig } from '../hooks/useRecordingConfig';
import toast from 'react-hot-toast';

const REGISTER_PROTOCOL_PS1 = `# Run in PowerShell from the folder where you extracted the recorder (once per PC user).
$ErrorActionPreference = "Stop"
$exe = Join-Path $PSScriptRoot "ETR.Video.Recorder.exe"
if (-not (Test-Path $exe)) { throw "ETR.Video.Recorder.exe not found in $PSScriptRoot" }
$protocol = "etr-video-recorder"
$base = "HKCU:\\SOFTWARE\\Classes\\$protocol"
New-Item -Path $base -Force | Out-Null
Set-ItemProperty -Path $base -Name "(Default)" -Value "URL:ETR Video Recorder"
Set-ItemProperty -Path $base -Name "URL Protocol" -Value ""
$icon = "$base\\DefaultIcon"
New-Item -Path $icon -Force | Out-Null
Set-ItemProperty -Path $icon -Name "(Default)" -Value "\\"$exe\\",0"
$cmd = "$base\\shell\\open\\command"
New-Item -Path $cmd -Force | Out-Null
Set-ItemProperty -Path $cmd -Name "(Default)" -Value "\\"$exe\\" \\"%1\\""
Write-Host "Registered $protocol for current user."`;

function copyText(text: string, label: string) {
  void navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
}

const InstallRecorder: React.FC = () => {
  const { data, isLoading, desktopRecorderEnabled } = useRecordingConfig();
  const install = data?.recorderInstall;
  const [stepDone, setStepDone] = useState<Record<number, boolean>>({});

  const hasDownload = !!install?.downloadUrl?.trim();
  const isSetupInstaller = (install?.downloadFileName ?? '').toLowerCase().endsWith('.exe');

  const steps = useMemo(
    () => [
      {
        title: 'Install .NET 8 Desktop Runtime (x64)',
        body: (
          <>
            The recorder needs the{' '}
            <strong>.NET 8 Desktop Runtime</strong> (not only ASP.NET). If recording fails to start, install
            it once per PC.
          </>
        ),
        action: install?.dotnetDesktopRuntimeUrl ? (
          <a
            href={install.dotnetDesktopRuntimeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            Download .NET runtime <ExternalLink className="h-4 w-4" />
          </a>
        ) : null,
      },
      {
        title: isSetupInstaller ? 'Download and run the installer' : 'Download the recorder',
        body: hasDownload ? (
          isSetupInstaller ? (
            <>
              Download <strong>ETR Video Recorder Setup</strong> ({install?.version ?? 'current'}), run it, and
              follow the wizard. It installs the app, registers <strong>Open desktop recorder</strong> from the
              portal, and creates shortcuts — no manual ZIP extract.
            </>
          ) : (
            <>
              Download the latest Windows package ({install?.version ?? 'current'}) and extract it to{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">{install?.defaultInstallDir}</code> or another
              folder you prefer.
            </>
          )
        ) : (
          <>
            Your administrator has not published a download link yet. Ask IT to run{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">scripts/build-recorder-installer.ps1</code>, host the
            setup EXE, and set <code className="text-xs bg-gray-100 px-1 rounded">Video:RecorderDownloadUrl</code>{' '}
            in Video.Api settings.
          </>
        ),
        action: hasDownload ? (
          <a href={install!.downloadUrl!} download={install?.downloadFileName}>
            <Button variant="primary" size="md" leftIcon={<Download className="h-4 w-4" />}>
              Download {install?.downloadFileName ?? 'recorder'}
            </Button>
          </a>
        ) : null,
      },
      ...(!isSetupInstaller
        ? [
            {
              title: 'Register “Open from portal” link',
              body: (
                <>
                  Run <strong>Register-Protocol.ps1</strong> from the extracted folder (included in the ZIP), or
                  copy the script below into PowerShell. This is per Windows user — Administrator rights are not
                  required.
                </>
              ),
              action: (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyText(REGISTER_PROTOCOL_PS1, 'Register script')}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy register script
                </Button>
              ),
            },
          ]
        : []),
      {
        title: 'Sign in on this PC',
        body: (
          <>
            Launch <strong>ETR.Video.Recorder.exe</strong>, sign in with the same Video Portal account you use in
            the browser. Your branch API URL is configured in{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">%ProgramData%\ETR\VideoRecorder\appsettings.json</code>{' '}
            (IT usually sets this when building the package).
          </>
        ),
      },
      {
        title: 'Record from a ticket',
        body: (
          <>
            In the portal go to <strong>My Tickets</strong> → accept a ticket → <strong>Record</strong> →{' '}
            <strong>Open desktop recorder</strong>. Record clips with <strong>Stop &amp; upload</strong>, reorder
            on the web page, then <strong>End Session &amp; Combine Videos</strong>.
          </>
        ),
        action: (
          <Link to="/my-tickets">
            <Button variant="primary" size="sm" leftIcon={<ClipboardList className="h-4 w-4" />}>
              Go to My Tickets
            </Button>
          </Link>
        ),
      },
    ],
    [hasDownload, install, isSetupInstaller]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!desktopRecorderEnabled) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card bg-amber-50 border-amber-200">
          <div className="card-content flex gap-3 text-amber-900">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p>Desktop recording is disabled on this environment. Contact your administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="text-center sm:text-left">
        <div className="inline-flex items-center justify-center sm:justify-start gap-2 text-primary-600 mb-2">
          <Monitor className="h-8 w-8" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Install desktop recorder</h1>
        <p className="mt-2 text-gray-600">
          One-time setup on each branch PC. After this, you record clips in the desktop app and finish sessions in
          the browser.
        </p>
        {install?.version && (
          <p className="mt-1 text-sm text-gray-500">Package version: {install.version}</p>
        )}
      </div>

      {!hasDownload && (
        <div className="card bg-amber-50 border-amber-200">
          <div className="card-content flex gap-3 text-amber-950 text-sm">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <p>
              Download is not configured on the server. An admin must host the setup EXE and set{' '}
              <code className="bg-white/60 px-1 rounded">Video:RecorderDownloadUrl</code> in API settings. You can
              still install manually if IT gave you the installer or ZIP.
            </p>
          </div>
        </div>
      )}

      <ol className="space-y-4">
        {steps.map((step, index) => {
          const n = index + 1;
          const done = stepDone[n];
          return (
            <li key={step.title} className="card">
              <div className="card-content">
                <div className="flex gap-4">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      done ? 'bg-emerald-100 text-emerald-800' : 'bg-primary-100 text-primary-800'
                    }`}
                  >
                    {done ? <CheckCircle2 className="h-5 w-5" /> : n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold text-gray-900">{step.title}</h2>
                    <p className="mt-1 text-sm text-gray-600">{step.body}</p>
                    {step.action && <div className="mt-3">{step.action}</div>}
                    <button
                      type="button"
                      className="mt-3 text-xs text-gray-500 hover:text-gray-800 underline"
                      onClick={() => setStepDone((s) => ({ ...s, [n]: !s[n] }))}
                    >
                      {done ? 'Mark incomplete' : 'Mark done'}
                    </button>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="card bg-blue-50 border-blue-200">
        <div className="card-content text-sm text-blue-900 space-y-2">
          <p className="font-semibold">For IT / administrators</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              Build:{' '}
              <code className="bg-white/50 px-1 rounded">.\scripts\build-recorder-installer.ps1 -CopyFfmpegFromPath</code>
            </li>
            <li>Upload <code className="bg-white/50 px-1 rounded">dist/ETR.Video.Recorder-Setup-*.exe</code> to HTTPS storage</li>
            <li>
              Set <code className="bg-white/50 px-1 rounded">Video:RecorderDownloadUrl</code> to the public HTTPS URL
            </li>
            <li>
              ZIP fallback:{' '}
              <code className="bg-white/50 px-1 rounded">.\scripts\build-recorder-zip.ps1</code> — see{' '}
              <code className="bg-white/50 px-1 rounded">docs/RECORDER_INSTALLER.md</code>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InstallRecorder;
